import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const ContentSection = () => {
  const { contentData, theme } = useLanguage();
  const isDark = theme === 'dark';

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <section
      id="content"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: isDark
          ? 'linear-gradient(180deg, #0f0f0f 0%, #141414 100%)'
          : 'linear-gradient(180deg, #F3EDE4 0%, #FAF7F2 100%)',
      }}
    >
      {/* Subtle background glow */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 700, height: 400,
        background: isDark
          ? 'radial-gradient(ellipse, rgba(184,134,11,0.06) 0%, transparent 70%)'
          : 'radial-gradient(ellipse, rgba(184,134,11,0.05) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6"
            style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.25)' }}
          >
            <BookOpen size={15} style={{ color: 'var(--accent-gold-400)' }} />
            <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.1em' }}>
              CONTEÚDO & REFLEXÕES
            </span>
          </div>

          <h2 className="heading-hero mb-5">{contentData.title}</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: 560, margin: '0 auto' }}>
            {contentData.subtitle}
          </p>
        </motion.div>

        {/* Articles list */}
        <div className="max-w-3xl mx-auto" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {contentData.articles.map((article, index) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ x: 6 }}
              className="group cursor-pointer"
              style={{
                padding: '1.5rem',
                borderRadius: 18,
                background: isDark ? 'rgba(30,30,30,0.5)' : 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                display: 'flex', alignItems: 'center', gap: '1.25rem',
                transition: 'border-color 0.3s, box-shadow 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(184,134,11,0.35)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(184,134,11,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Index number */}
              <div style={{
                width: 44, height: 44, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isDark ? '#1a1a1a' : '#F0EBE0',
                border: `1px solid ${isDark ? '#2a2a2a' : '#E0D8CC'}`,
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '1.1rem', fontWeight: 700,
                color: 'var(--accent-gold-400)',
              }}>
                {String(index + 1).padStart(2, '0')}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{
                    fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                    padding: '2px 10px', borderRadius: 100,
                    background: 'rgba(184,134,11,0.1)', color: 'var(--accent-gold-400)',
                    border: '1px solid rgba(184,134,11,0.2)',
                  }}>
                    {article.category}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={11} /> {formatDate(article.date)}
                  </span>
                </div>

                <h4 style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: '1.1rem', fontWeight: 700,
                  color: 'var(--text-primary)', marginBottom: 4, lineHeight: 1.3,
                }}>
                  {article.title}
                </h4>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {article.excerpt}
                </p>
              </div>

              {/* Arrow */}
              <motion.div
                style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isDark ? '#1e1e1e' : '#F0EBE0',
                  border: '1px solid transparent',
                  color: 'var(--text-muted)',
                  transition: 'all 0.3s',
                }}
                whileHover={{ background: 'var(--accent-gold-400)', color: '#fff', scale: 1.1 }}
              >
                <ArrowRight size={14} />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-14"
        >
          <p className="caption mb-4" style={{ color: 'var(--text-muted)' }}>
            Quer aprofundar seu conhecimento?
          </p>
          <motion.a
            href="#contact"
            className="btn-secondary inline-flex items-center gap-2"
            whileHover={{ scale: 1.04, y: -2 }}
            whileTap={{ scale: 0.98 }}
            style={{ textDecoration: 'none' }}
          >
            <Sparkles size={16} />
            Ver todos os conteúdos
            <ArrowRight size={16} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ContentSection;
